// İSG uzmanı portalı — Risk Değerlendirme detayı (api-contract §19). S33 Slice 6 —
// `PermitDetailPage.tsx`'in anatomisini AYNALAR: Breadcrumb → PageHeader (başlık + durum/risk
// seviyesi Tag'leri + yaşam döngüsü aksiyonları + Meta Düzenle) → `DetailLayout` (Bilgi Tile'ı
// solda, Tehlike kalemleri paneli sağda/altta — sekmesiz, DetailLayout'un en yalın hâli). Bilgi
// kartı: saha/alan/tarih/sonraki gözden geçirme/metodoloji/revizyon/değerlendiren/onaylayan.
// Gövde: **tehlike kalemleri** (sunucudan gelen score/level + DÖF + artık risk — `RiskItemsPanel`,
// kendi bespoke kart yığınıyla BU dilimde DOKUNULMADI — bkz. `docs/BACKLOG.md`).
// Tek `GET /risk-assessments/{uuid}` değerlendirme + kalemleri (+ DÖF) birlikte döndürür.

import {
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  DetailLayout,
  PageHeader,
  Spinner,
  Tag,
  currentCulture,
  resolveApiErrorMessage,
  riskApi,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Pencil, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { RiskAssessmentFormModal } from '../../components/risk/RiskAssessmentFormModal';
import { RiskItemsPanel } from '../../components/risk/RiskItemsPanel';
import { RiskLevelBadge } from '../../components/risk/RiskLevelBadge';
import { RiskLifecycleActions } from '../../components/risk/RiskLifecycleActions';
import { isRiskAssessmentEditable, riskStatusTone } from '../../components/risk/riskEnums';
import { formatDate, formatDateTime } from '../../lib/format';
import { riskKeys } from '../../lib/riskKeys';

/** Etiket + değer satırı (bilgi bloğu). Boş değer tire ile gösterilir. */
function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm text-fg">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

export function RiskAssessmentDetailPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const { assessmentUuid = '' } = useParams();

  const [editOpen, setEditOpen] = useState(false);

  const query = useQuery({
    queryKey: riskKeys.detail(assessmentUuid),
    queryFn: () => riskApi.getRiskAssessment(assessmentUuid),
    enabled: assessmentUuid.length > 0,
  });

  const detail = query.data;
  const assessment = detail?.assessment;
  const editable = assessment ? isRiskAssessmentEditable(assessment.status) : false;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('risk.title'), href: '/risk-assessments' },
          ...(assessment ? [{ label: assessment.title }] : []),
        ]}
      />

      {query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('risk.detail.loading')} />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <ShieldAlert className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/risk-assessments">{t('risk.detail.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : detail && assessment ? (
        <>
          <PageHeader
            title={assessment.title}
            id={assessment.area ?? undefined}
            tags={
              <>
                <Tag color={riskStatusTone(assessment.status)}>
                  {t(`risk.status.${assessment.status}`)}
                </Tag>
                {assessment.highestLevel ? (
                  <RiskLevelBadge level={assessment.highestLevel} />
                ) : null}
              </>
            }
            helperText={t('risk.detail.revision', { revision: assessment.revisionNo })}
            actions={
              <ButtonSet>
                {editable ? (
                  <Button variant="ghost" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                    {t('risk.detail.edit')}
                  </Button>
                ) : null}
                <RiskLifecycleActions assessment={assessment} />
              </ButtonSet>
            }
          />

          <DetailLayout
            meta={
              <Card className="col-span-2">
                <CardContent className="flex flex-col gap-4">
                  <h2 className="text-sm font-semibold text-fg">
                    {t('permits.detail.section.details')}
                  </h2>
                  <dl className="grid grid-cols-2 gap-4">
                    <Fact label={t('risk.detail.field.site')} value={assessment.siteName} />
                    <Fact label={t('risk.detail.field.area')} value={assessment.area} />
                    <Fact
                      label={t('risk.detail.field.assessmentDate')}
                      value={formatDate(assessment.assessmentDate, culture)}
                    />
                    <Fact
                      label={t('risk.detail.field.nextReviewDate')}
                      value={
                        assessment.nextReviewDate
                          ? formatDate(assessment.nextReviewDate, culture)
                          : null
                      }
                    />
                    <Fact
                      label={t('risk.detail.field.methodology')}
                      value={t(`risk.methodology.${assessment.methodology}`)}
                    />
                    <Fact
                      label={t('risk.detail.field.revision')}
                      value={String(assessment.revisionNo)}
                    />
                    <Fact
                      label={t('risk.detail.field.assessedBy')}
                      value={assessment.assessedByName}
                    />
                    <Fact
                      label={t('risk.detail.field.approvedBy')}
                      value={assessment.approvedByName}
                    />
                    <Fact
                      label={t('risk.detail.field.approvedAt')}
                      value={
                        assessment.approvedAt
                          ? formatDateTime(assessment.approvedAt, culture)
                          : null
                      }
                    />
                  </dl>
                </CardContent>
              </Card>
            }
            content={
              <Card>
                <CardContent>
                  <RiskItemsPanel
                    assessment={assessment}
                    items={detail.items}
                    editable={editable}
                  />
                </CardContent>
              </Card>
            }
          />

          {editable ? (
            <RiskAssessmentFormModal
              open={editOpen}
              onOpenChange={setEditOpen}
              assessment={assessment}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
